import * as React from 'react';
import { useCallback, useContext } from 'react';
import styled from 'styled-components';

import { AccountInfoContext } from '../account/account-info';
import { InteractiveModal } from '../ui-elements/modals/InteractiveModal';
import { colors } from '../ui-elements/Main/MainTheme';
import { usePhoneVerification, PhoneVerifyResult } from './use-phone-verification.hook';
import { PhoneVerifyForm } from './PhoneVerifyForm';

export type TrialPhoneVerifyResult = PhoneVerifyResult;

interface TrialPhoneVerifyModalProps {
  show: boolean;
  onClose: () => void;
  onVerified: (result?: TrialPhoneVerifyResult) => void;
}

export const TrialPhoneVerifyModal = ({
  show,
  onClose,
  onVerified,
}: TrialPhoneVerifyModalProps) => {
  const { subdomain } = useContext(AccountInfoContext);
  const verify = usePhoneVerification({ subdomain, onVerified });
  const { reset } = verify;

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <InteractiveModal hide={!show} width="540px" height="auto">
      <Container>
        <Header>Verify your mobile phone</Header>
        <Subheader>
          Free trial accounts can only send SMS to their own verified mobile
          number. Add yours to begin testing.
        </Subheader>
        <PhoneVerifyForm verify={verify} onCancel={handleClose} onConfirmed={handleClose} />
      </Container>
    </InteractiveModal>
  );
};

const Container = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  /* Roomier padding — gives the title + form some breathing room and
     matches the proportions of the in-app verify pill we use elsewhere. */
  padding: 40px 44px 36px;
  width: 100%;
  font-family: Montserrat, sans-serif;
`;

const Header = styled.h2`
  font-size: 26px;
  /* Bolder title (was 700). Reads as the headline rather than a label. */
  font-weight: 800;
  color: ${colors.darkGreen};
  margin-top: 0;
  margin-bottom: 0;
  line-height: 1.25;
  letter-spacing: -0.2px;
`;

const Subheader = styled.p`
  font-size: 14px;
  color: ${colors.gray};
  margin-top: 14px;
  margin-bottom: 22px;
  line-height: 1.5;
`;
